import {
    type WeekendPattern,
    type ShiftLimit,
    RequestStatus,
    ShiftPreference,
} from '@features/requests/types';
import {
    Drafts,
    CalendarToday,
    History,
    BeachAccess,
    Paid,
    EventAvailable,
} from '@mui/icons-material';

/**
 * Request Constants
 * Configuration values for request validation and processing
 */

// Attachment limits
export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024; // 5MB in bytes

// Allowed MIME types for attachments
export const ALLOWED_ATTACHMENT_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
] as const;

export type AllowedAttachmentType = (typeof ALLOWED_ATTACHMENT_TYPES)[number];

// Weekend patterns - Saturday (6) and Sunday (0) are blocked by default
export const WEEKEND_PATTERNS: WeekendPattern[] = [
    { dayOfWeek: 0, isBlocked: true }, // Sunday
    { dayOfWeek: 6, isBlocked: true }, // Saturday
];

// Shift limits - Maximum hours per week for shift preferences
export const SHIFT_LIMITS: ShiftLimit = {
    maxHoursPerWeek: 40,
};

// Notification messages in Norwegian
export const NOTIFICATION_MESSAGES = {
    REQUEST_SENT: 'Søknad sendt. Venter på godkjenning.',
    REQUEST_CANCELLED: 'Søknad kansellert.',
    REQUEST_SAVED_AS_DRAFT: 'Søknad lagret som utkast.',
    REQUEST_UPDATED: 'Søknad oppdatert.',
} as const;

// Dialog texts for resubmitting a declined request
export const RESUBMIT_DIALOG = {
    TITLE: 'Send søknad på nytt',
    TEXT: 'Denne søknaden ble avslått. Vil du sende den på nytt? Den sendes til ny vurdering av leder.',
    CONFIRM: 'Send på nytt',
} as const;

// Status colors for Badge component (keys match the API RequestStatus values)
export const STATUS_COLORS: Record<RequestStatus, BadgeVariant> = {
    [RequestStatus.DRAFT]: 'neutral',
    [RequestStatus.SUBMITTED]: 'info',
    [RequestStatus.APPROVED]: 'success',
    [RequestStatus.REJECTED]: 'error',
    [RequestStatus.CANCELLED]: 'warning',
    [RequestStatus.MODIFIED]: 'success',
};

// Shift preference labels in Norwegian
export const SHIFT_PREFERENCE_LABELS = {
    MORNING: 'Morgen',
    AFTERNOON: 'Ettermiddag',
    NIGHT: 'Kveld',
    WEEKEND: 'Helg',
    OTHER: 'Annet',
} as const;

// BadgeVariant type (same as @dynamon-as/ui)
// neutral | primary | info | success | warning | error
export type BadgeVariant =
    'neutral' | 'primary' | 'info' | 'success' | 'warning' | 'error';

// Tab configuration for request filtering
export const REQUEST_TABS = [
    { value: 'drafts', label: 'Utkast', Icon: Drafts },
    { value: 'current', label: 'Aktuelle', Icon: CalendarToday },
    { value: 'past', label: 'Tidligere', Icon: History },
] as const;

export type RequestTabValue = (typeof REQUEST_TABS)[number]['value'];

// Icons per request type (used in list cards)
export const REQUEST_TYPE_ICONS = {
    VACATION: BeachAccess,
    PAID_LEAVE: Paid,
    SHIFT_PREFERENCE: EventAvailable,
} as const;

// Status → Badge variant mapping (used in list cards)
export const STATUS_BADGE_VARIANTS: Record<RequestStatus, BadgeVariant> = {
    [RequestStatus.DRAFT]: 'neutral',
    [RequestStatus.SUBMITTED]: 'info',
    [RequestStatus.APPROVED]: 'success',
    [RequestStatus.REJECTED]: 'error',
    [RequestStatus.CANCELLED]: 'warning',
    [RequestStatus.MODIFIED]: 'success',
};

// Human-readable shift labels keyed by enum value ('D', 'A', ...)
export const SHIFT_PREFERENCE_DISPLAY_LABELS: Record<ShiftPreference, string> =
    {
        [ShiftPreference.MORNING]: 'Morgen',
        [ShiftPreference.AFTERNOON]: 'Ettermiddag',
        [ShiftPreference.NIGHT]: 'Kveld',
        [ShiftPreference.WEEKEND]: 'Helg',
        [ShiftPreference.OTHER]: 'Annet',
    };
